/**
 * JSXGraph figure renderer.
 *
 * Renders a Figure spec (function curves, points, asymptotes) using
 * JSXGraph — deterministic, mathematically correct. The spec shape
 * comes directly from the backend's Figure.model_dump().
 */
import { useEffect, useRef, useState } from "react";
import JXG from "jsxgraph";
import type { FigureSpec } from "../types";
import { ChartFigure } from "./Charts";
import { saNumber } from "../lib/saNumber";

// JSXGraph's declaration file omits several supported board options used by
// the renderer, so keep the third-party API untyped at this boundary.
const JSXGraph: any = JXG;

const CURVE_COLOR = "#22354d";
const POINT_COLOR = "#c62828";
const ASYMPTOTE_COLOR = "#9aa0a6";

interface FigureProps {
  spec: FigureSpec;
  height?: number;
  maxWidth?: number;
}

const SUPPORTED = new Set(["function", "geometry"]);

export function Figure({ spec, height = 340, maxWidth = 520 }: FigureProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const boardRef = useRef<any>(null);
  const [renderError, setRenderError] = useState(false);
  const supported = SUPPORTED.has(spec.type);

  useEffect(() => {
    if (!supported) return; // charts are rendered as a table fallback below
    const el = containerRef.current;
    if (!el) return;
    setRenderError(false);

    const isGeom = spec.type === "geometry";

    // Compute bounding box. A graph that starts at an axis (domain or range
    // from 0, as most real-life graphs do) would put that axis on the edge of
    // the board, where its tick numbers fall off: pad every side a little.
    // Curves are still drawn over the domain only (renderFunction), so the
    // margin never shows negative petrol or a negative cost.
    const x1 = spec.domain?.[1] ?? 5, y1 = spec.range?.[1] ?? 5;
    let x0 = spec.domain?.[0] ?? -5, y0 = spec.range?.[0] ?? -5;
    // A domain starting just right of 0 (800/x from x = 1) keeps the y-axis,
    // and its numbers, on the board.
    if (!isGeom && x0 > 0 && x0 <= (x1 - x0) * 0.25) x0 = 0;
    if (!isGeom && y0 > 0 && y0 <= (y1 - y0) * 0.25) y0 = 0;
    const px = isGeom ? 0 : (x1 - x0) * 0.08, py = isGeom ? 0 : (y1 - y0) * 0.08;
    const bb = spec.boundingbox ?? [x0 - px, y1 + py, x1 + px, y0 - py];

    let board: any = null;
    try {
      board = JSXGraph.JSXGraph.initBoard(el, {
        boundingbox: bb,
        axis: isGeom ? spec.show_axes ?? false : true,
        grid: isGeom ? spec.show_grid ?? false : true,
        showNavigation: false,
        showCopyright: false,
        keepAspectRatio: isGeom ? spec.keep_aspect ?? true : false,
        pan: { enabled: false },
        zoom: { enabled: false },
        // Figure labels are untrusted text, never HTML or expressions.
        text: { display: "internal", parse: false },
        label: { display: "internal", parse: false },
        // Learners are South African: axis ticks read 0,5 not 0.5.
        defaultAxes: {
          x: { ticks: { intl: { enabled: true, locale: "en-ZA" } } },
          y: { ticks: { intl: { enabled: true, locale: "en-ZA" } } },
        },
      });

      if (isGeom) {
        renderGeometry(board, spec);
      } else if (spec.interactive === "secant") {
        renderSecant(board, spec, bb);
      } else {
        renderFunction(board, spec, bb);
      }

      boardRef.current = board;
    } catch {
      if (board) JSXGraph.JSXGraph.freeBoard(board);
      setRenderError(true);
    }

    return () => {
      if (boardRef.current) {
        JSXGraph.JSXGraph.freeBoard(boardRef.current);
        boardRef.current = null;
      }
    };
  }, [spec]);

  // Original exam pages go to the marker only; they are never drawn for the learner.
  if (spec.type === "exam_pages") return null;

  // Diagrams cropped unchanged from the exam paper, shown as the paper prints them.
  if (spec.type === "paper_diagrams") {
    return (
      <div className="paper-diagrams" style={{ maxWidth }}>
        {(spec.images ?? []).map((src) => (
          <img key={src} src={src} alt={`Diagram from the exam paper${spec.title ? `: ${spec.title}` : ""}`} />
        ))}
      </div>
    );
  }

  if (!supported) {
    // Try the SVG chart renderer; fall back to a data table if it can't draw.
    const chart = ChartFigure({ spec });
    return (
      <div style={{ width: "100%", maxWidth, margin: "0 auto" }}>
        {chart ?? <FigureFallback spec={spec} maxWidth={maxWidth} />}
      </div>
    );
  }

  if (renderError) {
    return <FigureFallback spec={spec} maxWidth={maxWidth} />;
  }

  return (
    <div
      ref={containerRef}
      className="jxg-figure"
      style={{ width: "100%", maxWidth, height, margin: "0 auto" }}
    />
  );
}

/** Build a plain table from a chart spec so the data is never lost when the
 *  chart type isn't drawable (bar_chart, line_graph, pie_chart, …). */
function chartTable(spec: FigureSpec): { head: string[]; body: string[][] } | null {
  const seg = (spec as any).segments as any[] | undefined;
  // Pie chart: segments are {label, value, percentage} objects.
  if (Array.isArray(seg) && seg.length && !Array.isArray(seg[0]) && typeof seg[0] === "object") {
    return {
      head: ["Category", "Value", "%"],
      body: seg.map((s) => [
        String(s.label ?? ""),
        s.value != null ? String(s.value) : "",
        s.percentage != null ? `${s.percentage}%` : "",
      ]),
    };
  }
  const series = spec.series;
  if (Array.isArray(series) && series.length) {
    // Bar chart: categories × series values.
    if (spec.categories && series.some((s) => s.values)) {
      return {
        head: ["", ...series.map((s) => s.label ?? "")],
        body: spec.categories.map((c, i) => [
          c,
          ...series.map((s) => (s.values?.[i] != null ? String(s.values[i]) : "")),
        ]),
      };
    }
    // Line/step graph: list each series' points.
    if (series.some((s) => s.points)) {
      return {
        head: ["Series", "Points (x, y)"],
        body: series.map((s) => [
          s.label ?? "",
          (s.points ?? []).map((p) => `(${p[0]}, ${p[1]})`).join("  "),
        ]),
      };
    }
  }
  return null;
}

function FigureFallback({ spec, maxWidth }: { spec: FigureSpec; maxWidth: number }) {
  const table = chartTable(spec);
  return (
    <div className="figure-fallback" style={{ maxWidth, margin: "0 auto" }}>
      {table ? (
        <div className="figure-fallback-scroll">
          <table className="figure-data-table">
            <thead>
              <tr>{table.head.map((h, i) => <th key={i}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {table.body.map((row, i) => (
                <tr key={i}>{row.map((c, j) => <td key={j}>{c}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="figure-fallback-note">
          A visual for this question isn't available in this view yet — the figures
          you need are described in the question text.
        </p>
      )}
    </div>
  );
}

/**
 * Secant → tangent, driven by a draggable h.
 *
 * The learner drags h towards 0 and watches the secant through (a, f(a)) and
 * (a+h, f(a+h)) settle onto the tangent. That *is* the limit in the definition
 * of f'(a) — shown happening rather than asserted, which is the whole
 * difficulty of teaching first principles.
 *
 * The readout deliberately shows the difference quotient's current value and
 * its gap from the true derivative, so "approaches" is a number the learner can
 * watch shrink instead of a word.
 */
function renderSecant(board: any, spec: FigureSpec, bb: number[]) {
  const term = spec.exprs?.[0];
  if (!term) return;

  // Compile the expression once into a JS function of x.
  let f: (x: number) => number;
  try {
    f = board.jc.snippet(term, true, "x", true);
    if (!Number.isFinite(f(0))) throw new Error("non-finite at 0");
  } catch {
    // Unusable expression — fall back to the plain curve rather than a blank box.
    renderFunction(board, spec, bb);
    return;
  }

  const a = spec.at ?? (bb[0] + bb[2]) / 2;
  const [hMin, hMax] = spec.h_range?.length === 2 ? spec.h_range : [0.05, 3];
  const hStart = spec.h_start ?? hMax;

  board.create("functiongraph", [term, bb[0], bb[2]], {
    strokeColor: CURVE_COLOR, strokeWidth: 2, highlight: false,
  });

  // Slider sits inside the board, along the bottom.
  const y = bb[3] + (bb[1] - bb[3]) * 0.08;
  const h = board.create(
    "slider",
    [[bb[0] + (bb[2] - bb[0]) * 0.1, y], [bb[0] + (bb[2] - bb[0]) * 0.6, y], [hMin, hStart, hMax]],
    { name: "h", snapWidth: 0.01, precision: 2, strokeColor: POINT_COLOR,
      fillColor: POINT_COLOR, label: { fontSize: 13 }, highlight: false },
  );

  const A = board.create("point", [a, f(a)], {
    name: "a", size: 3, fixed: true, strokeColor: POINT_COLOR,
    fillColor: POINT_COLOR, highlight: false, label: { offset: [-6, 12], fontSize: 12 },
  });
  const B = board.create(
    "point",
    [() => a + h.Value(), () => f(a + h.Value())],
    { name: "a + h", size: 3, fixed: true, strokeColor: POINT_COLOR,
      fillColor: POINT_COLOR, highlight: false, label: { offset: [8, 10], fontSize: 12 } },
  );

  board.create("line", [A, B], {
    strokeColor: POINT_COLOR, strokeWidth: 2, straightFirst: true,
    straightLast: true, highlight: false,
  });

  if (spec.show_tangent !== false) {
    // Symmetric difference at a very small step: accurate without symbolic
    // differentiation, and stable for the polynomials these questions use.
    const eps = 1e-6;
    const slope = (f(a + eps) - f(a - eps)) / (2 * eps);
    board.create("line", [[a, f(a)], [a + 1, f(a) + slope]], {
      strokeColor: ASYMPTOTE_COLOR, strokeWidth: 2, dash: 2,
      straightFirst: true, straightLast: true, highlight: false, layer: 3,
    });
    board.create(
      "text",
      [bb[0] + (bb[2] - bb[0]) * 0.04, bb[1] - (bb[1] - bb[3]) * 0.08,
       () => {
         const hv = h.Value();
         const q = (f(a + hv) - f(a)) / hv;
         return `slope of secant = ${saNumber(q, 3)}  ·  gap from f'(a) = ${saNumber(Math.abs(q - slope), 3)}`;
       }],
      { fontSize: 13, color: "#16202e", fixed: true, highlight: false },
    );
  }
}

/** Function graphs: curves, asymptotes, marked points. */
function renderFunction(board: any, spec: FigureSpec, bb: number[]) {
  // Over the stated domain, not the padded board.
  const from = spec.domain?.[0] ?? bb[0], to = spec.domain?.[1] ?? bb[2];
  // Axis titles ("Petrol (litres)", "Cost (R)") at the far end of each axis.
  const textStyle = { fixed: true, highlight: false, fontSize: 12, strokeColor: "#4a5666" };
  if (spec.x_label) {
    board.create("text", [bb[2], 0, String(spec.x_label)], {
      ...textStyle, anchorX: "right", anchorY: "bottom",
    });
  }
  if (spec.y_label) {
    board.create("text", [0, bb[1], String(spec.y_label)], {
      ...textStyle, anchorX: "left", anchorY: "top",
    });
  }
  (spec.exprs ?? []).forEach((term: string) => {
    try {
      board.create("functiongraph", [term, from, to], {
        strokeColor: CURVE_COLOR,
        strokeWidth: 2,
        highlight: false,
      });
    } catch {
      /* skip malformed term */
    }
  });
  (spec.vlines ?? []).forEach((x: number) => {
    board.create("line", [[x, bb[3]], [x, bb[1]]], {
      dash: 2, strokeColor: ASYMPTOTE_COLOR, strokeWidth: 1,
      straightFirst: true, straightLast: true, fixed: true, highlight: false, layer: 3,
    });
  });
  (spec.hlines ?? []).forEach((y: number) => {
    board.create("line", [[bb[0], y], [bb[2], y]], {
      dash: 2, strokeColor: ASYMPTOTE_COLOR, strokeWidth: 1, fixed: true, highlight: false, layer: 3,
    });
  });
  (spec.points ?? []).forEach((p: (number | string)[]) => {
    board.create("point", [p[0], p[1]], {
      name: (p[2] as string) ?? "", size: 2, fixed: true,
      strokeColor: POINT_COLOR, fillColor: POINT_COLOR, highlight: false,
      label: { offset: [8, 8], fontSize: 12 },
    });
  });
}

/** Geometry diagrams: named vertices, segments, polygons, angle marks, labels. */
function renderGeometry(board: any, spec: FigureSpec) {
  const VERTEX = "#16202e";
  const pts: Record<string, any> = {};
  (spec.points ?? []).forEach((p: (number | string)[]) => {
    const name = String(p[2] ?? "");
    // Names starting with "_" are invisible construction points (usable by
    // segments/polygons for box-plots, histograms, etc. without cluttering).
    const hidden = name.startsWith("_");
    pts[name] = board.create("point", [Number(p[0]), Number(p[1])], {
      name: hidden ? "" : name, visible: !hidden,
      size: 1, fixed: true, strokeColor: VERTEX, fillColor: VERTEX,
      highlight: false, label: { offset: [7, 7], fontSize: 13, strokeColor: VERTEX },
    });
  });
  const P = (k: any) => (typeof k === "string" ? pts[k] : k);

  // Circles (centre + radius), drawn behind other marks
  (spec.circles ?? []).forEach((c: any) => {
    const center = c.center ?? c.centre ?? [0, 0];
    board.create("circle", [[Number(center[0]), Number(center[1])], Number(c.r)], {
      strokeColor: VERTEX, strokeWidth: 1.5, fixed: true, highlight: false,
      fillColor: "#cfd8e3", fillOpacity: 0.12,
    });
  });
  // Filled regions first (behind lines)
  (spec.polygons ?? []).forEach((poly: string[]) => {
    board.create("polygon", poly.map(P), {
      fillColor: "#cfd8e3", fillOpacity: 0.3, fixed: true, highlight: false,
      borders: { visible: false }, vertices: { visible: false }, layer: 1,
    });
  });
  (spec.dashed_segments ?? []).forEach((s: any[]) => {
    board.create("segment", [P(s[0]), P(s[1])], {
      strokeColor: "#7d8794", strokeWidth: 1, dash: 2, fixed: true, highlight: false,
    });
  });
  (spec.segments ?? []).forEach((s: any[]) => {
    board.create("segment", [P(s[0]), P(s[1])], {
      strokeColor: VERTEX, strokeWidth: 1.5, fixed: true, highlight: false,
    });
  });
  (spec.right_angles ?? []).forEach((a: string[]) => {
    board.create("angle", [P(a[0]), P(a[1]), P(a[2])], {
      type: "square", radius: 0.45, fixed: true, highlight: false,
      strokeColor: POINT_COLOR, fillColor: POINT_COLOR, fillOpacity: 0.5, name: "",
    });
  });
  const hidden = (c: number[]) =>
    board.create("point", [Number(c[0]), Number(c[1])], { visible: false, fixed: true, name: "" });
  (spec.angles ?? []).forEach((a: any) => {
    let from, vertex, to, label;
    if (Array.isArray(a)) {
      from = P(a[0]); vertex = P(a[1]); to = P(a[2]); label = String(a[3] ?? "");
    } else if (a && a.vertex) {
      // dict form with coordinate arrays: {vertex, from, to, label}
      from = hidden(a.from); vertex = hidden(a.vertex); to = hidden(a.to);
      label = String(a.label ?? "");
    } else {
      return;
    }
    board.create("angle", [from, vertex, to], {
      radius: 0.9, fixed: true, highlight: false, name: label, withLabel: true,
      strokeColor: CURVE_COLOR, fillColor: CURVE_COLOR, fillOpacity: 0.18,
      label: { fontSize: 14, strokeColor: VERTEX },
    });
  });
  (spec.labels ?? []).forEach((l: (number | string)[]) => {
    board.create("text", [Number(l[0]), Number(l[1]), String(l[2] ?? "")], {
      fixed: true, fontSize: 13, highlight: false, anchorX: "middle", cssStyle: "font-weight:600;",
    });
  });
}
